import React, { useCallback, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import { useA11ySelector } from '@sg/a11y-helpers';
import {
  BodyText,
  Icon,
  LoadingAnimation,
  ModalDialogue,
  theme,
  useResponsive,
  useToggleState,
} from '@sg/garnish';
import {
  LoyaltyHomeProgressBar,
  LoyaltyRewardTerms,
} from '@sg/shared-fe/features/loyalty';

import { Loyalty, LOYALTY_PALETTES } from '@order/features/loyalty';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { AppFooter, OrderStatusBar, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  useLoyaltyContent,
  useLoyaltyPremiumStatus,
  useNavigateToLastOrderMenu,
  useTrademark,
} from '../../hooks';
import { JoinSgRewardsScreen } from '../JoinSgRewardsScreen';
import {
  LoyaltyHomeChallenges,
  LoyaltyHomeFaq,
  LoyaltyHomeRewards,
  LoyaltyHowItWorksModal,
  LoyaltyPointHistoryModal,
} from './components';
import {
  useHomeProgressBenefit,
  useLoyaltyHomeScreenReferrer,
  useLoyaltyInfo,
  useTermsBeingShown,
} from './hooks';
import { useOptInChallengeOnFocus } from './hooks/useOptInChallengeOnFocus';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyHomeScreen = () => {
  const isLoggedIn = useIsLoggedIn();

  useTrackEffect('loyalty.home.view');
  useLoyaltyHomeScreenReferrer();

  if (!isLoggedIn) return <SignedOutView />;

  return <LoyaltyHomeScreenSignedIn />;
};

const LoyaltyHomeScreenSignedIn = () => {
  const { currentBreakpoint, minWidth } = useResponsive();
  const isScreenFocused = useIsFocused();
  const isForgotToScanEnabled = useFeatureFlag(
    'CELS-4258-forgot-to-scan-enabled',
  );

  const rewardsBarContainerA11ySelector = useA11ySelector(
    'loyalty.rewards-bar.container',
  );

  // ─── Auto Opt In ─────────────────────────────────────────────────────

  useOptInChallengeOnFocus();

  // ─── Premium ──────────────────────────────────────────────────────────────

  const { isPremium } = useLoyaltyPremiumStatus();
  const premiumBgColor = isPremium ? LOYALTY_PALETTES.premiumBgColor : null;
  const premiumFgColor = isPremium ? LOYALTY_PALETTES.premiumFgColor : null;

  // ─── GraphQL ──────────────────────────────────────────────────────────────

  const {
    isLoading: isLoyaltyInfoLoading,
    isStale: isLoyaltyInfoStale,
    hasFailedToLoad,
    hasNoPointsRewards,
    firstName,
    availablePoints,
    pendingPoints,
    totalPoints,
    challenges,
    rewards,
    pointsRewards,
    isOptedOut,
    refetch: refetchLoyaltyInfo,
  } = useLoyaltyInfo({ pause: !isScreenFocused });

  const hasChallenges = challenges.length > 0;
  const hasRewards = rewards.length > 0;

  // ─── Styles ───────────────────────────────────────────────────────────────

  const rewardsStyleXS = [
    premiumBgColor,
    styles.challengesRewardsPaddingXS,
    styles.paddingWithContentBottom,
  ];

  const challengesStyleXS = [
    premiumBgColor,
    styles.challengesRewardsPaddingXS,
    hasRewards ? styles.paddingWithContentTop : undefined,
  ];

  const benefitsStyle = [
    styles.loyaltyInfoBenefits,
    isPremium ? styles.premiumBenefitsBorder : undefined,
  ];

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    homeHowItWorksSM,
    homeHowItWorksDisclaimer,
    homeProgressBenefits,
    homeProgressBenefitsDisclaimer: benefitsDisclaimer,
    termsAndConditionsUrl,
  } = useLoyaltyContent({
    availablePoints,
  });

  const trademark = useTrademark();

  // ─── Progress Benefit ─────────────────────────────────────────────────────

  const progressBenefit = useHomeProgressBenefit({
    availablePoints,
    pendingPoints,
    homeProgressBenefits,
  });

  // ─── Points History Modal ─────────────────────────────────────────────────

  const {
    value: isShowingPointsHistoryModal,
    toggleOn: showPointsHistoryModalToggleOn,
    toggleOff: hidePointsHistoryModal,
  } = useToggleState();

  // ─── How it Works Modal ───────────────────────────────────────────────────

  const {
    value: isShowingHowItWorksModal,
    toggleOn: showHowItWorksModalToggleOn,
    toggleOff: hideHowItWorksModal,
  } = useToggleState();

  // ─── Telemetry ────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const showPointsHistoryModal = useCallback(() => {
    track('loyalty.pointhistory_viewed');
    showPointsHistoryModalToggleOn();
  }, [track, showPointsHistoryModalToggleOn]);

  const showHowItWorksModal = useCallback(() => {
    track('loyalty.howitworks.cta_tapped');
    showHowItWorksModalToggleOn();
  }, [track, showHowItWorksModalToggleOn]);

  const trackBenefitsOpened = useCallback(() => {
    track('loyalty.accordion_pressed');
  }, [track]);

  const handleOnLinkPress = useCallback(() => {
    track('reward.terms.cta_tapped');
  }, [track]);

  // ─── Terms Modal ──────────────────────────────────────────────────────────

  const { termsBeingShown, showTerms, hideTerms } = useTermsBeingShown();

  // ─── Navigation ───────────────────────────────────────────────────────────

  const navigation = useNavigation();
  const navigateToLastOrderMenu = useNavigateToLastOrderMenu();

  const clearModals = useCallback(() => {
    hideTerms();
    hidePointsHistoryModal();
    hideHowItWorksModal();
  }, [hideHowItWorksModal, hidePointsHistoryModal, hideTerms]);

  const startOrder = useCallback(() => {
    track('reward.start.cta_tapped');
    clearModals();
    navigateToLastOrderMenu();
  }, [clearModals, navigateToLastOrderMenu, track]);

  const goToForgotToScan = useCallback(() => {
    track('forgot-to-scan.cta_tapped', { location: 'loyalty' });
    navigation.navigate('Modal', {
      screen: 'ForgotToScan',
    });
  }, [navigation, track]);

  useEffect(() => {
    navigation.setOptions({ headerShown: !currentBreakpoint.isXS });
  }, [navigation, currentBreakpoint.isXS]);

  useFocusEffect(
    useCallback(() => {
      return clearModals;
    }, [clearModals]),
  );

  // ──────────────────────────────────────────────────────────────────────────

  if (isOptedOut) {
    return <JoinSgRewardsScreen />;
  }

  if (hasFailedToLoad || hasNoPointsRewards) {
    return (
      <>
        <Loyalty.Home.ErrorState
          isLoading={isLoyaltyInfoLoading || isLoyaltyInfoStale}
          refetch={refetchLoyaltyInfo}
        />
        {minWidth.isSM ? <AppFooter /> : null}
      </>
    );
  }

  if (isLoyaltyInfoLoading) return <LoadingAnimation />;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Loyalty.Home.Container
      availablePoints={availablePoints}
      palette={isPremium ? 'dark-kale' : 'cream'}
      style={premiumBgColor}
    >
      {isScreenFocused && isPremium ? (
        <OrderStatusBar barStyle="light-content" />
      ) : null}

      {/* Header */}
      <Loyalty.Home.Header.Container>
        <Loyalty.Home.Header.Content>
          <Loyalty.Home.Header.FirstName
            firstName={firstName}
            style={premiumFgColor}
          />

          <Loyalty.Home.Header.PointsContainer>
            <Loyalty.Home.Header.AvailablePoints
              availablePoints={availablePoints}
              style={premiumFgColor}
            />

            {minWidth.isSM && isPremium ? (
              <Icon name="IconGoat" width={60} height={60} />
            ) : null}
          </Loyalty.Home.Header.PointsContainer>
        </Loyalty.Home.Header.Content>

        {currentBreakpoint.isXS && isPremium ? (
          <Icon name="IconGoat" width={60} height={60} />
        ) : null}
      </Loyalty.Home.Header.Container>

      {/* Benefits & Progress Bar */}

      {progressBenefit && pointsRewards.length > 0 && minWidth.isSM ? (
        <Loyalty.Home.ProgressBenefit.SM
          style={premiumFgColor}
          progressBenefit={progressBenefit}
        />
      ) : null}

      {pointsRewards.length > 0 ? (
        <LoyaltyHomeProgressBar
          palette={isPremium ? 'dark' : 'light'}
          availablePoints={availablePoints}
          totalPoints={totalPoints}
          pointsRewards={pointsRewards}
          {...rewardsBarContainerA11ySelector.props}
        />
      ) : null}

      {progressBenefit && pointsRewards.length > 0 && !minWidth.isSM ? (
        <Loyalty.Home.ProgressBenefit.XS
          palette={isPremium ? 'dark' : 'light'}
          progressBenefit={progressBenefit}
          benefitsCount={pointsRewards.length}
          onBenefitsOpened={trackBenefitsOpened}
        >
          <Loyalty.InfoBenefits
            availablePoints={availablePoints}
            loyaltyInfoBenefits={pointsRewards}
            isLoading={isLoyaltyInfoLoading}
            hasError={hasFailedToLoad}
            style={benefitsStyle}
            outerContainerStyle={styles.loyaltyInfoBenefitsOuterContainer}
            disclaimerPosition="inside"
            disclaimer={benefitsDisclaimer}
            refetch={refetchLoyaltyInfo}
          />
        </Loyalty.Home.ProgressBenefit.XS>
      ) : null}

      {benefitsDisclaimer && minWidth.isSM ? (
        <Loyalty.Home.LegalDisclaimer
          disclaimer={benefitsDisclaimer}
          style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
        />
      ) : null}

      {/* Point History & How It Works Buttons */}

      <Loyalty.Home.Body isPremium={isPremium}>
        {minWidth.isSM ? (
          <Loyalty.PointsHistory.CtaButton
            palette={isPremium ? 'cream' : 'secondary'}
            onPress={showPointsHistoryModal}
          />
        ) : null}

        {currentBreakpoint.isXS ? (
          <>
            <Loyalty.PointsHistory.CtaLink
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              onPress={showPointsHistoryModal}
            />

            <BodyText
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              sizeMatch={['14']}
            >
              •
            </BodyText>

            <Loyalty.HowItWorks.Cta
              style={isPremium ? LOYALTY_PALETTES.premiumFgColor : undefined}
              onPress={showHowItWorksModal}
            />
          </>
        ) : null}
      </Loyalty.Home.Body>

      {/* Challenges & Rewards */}

      {currentBreakpoint.isXS ? (
        <>
          <LoyaltyHomeRewards
            style={rewardsStyleXS}
            hasRewards={hasRewards}
            rewards={rewards}
            startOrder={startOrder}
            showTerms={showTerms}
          />
          <LoyaltyHomeChallenges
            style={challengesStyleXS}
            hasChallenges={hasChallenges}
            isPremium={isPremium}
            challenges={challenges}
            startOrder={startOrder}
          />
        </>
      ) : null}

      {minWidth.isSM ? (
        <>
          <LoyaltyHomeChallenges
            style={[premiumBgColor, styles.challengesRewardsPaddingSM]}
            hasChallenges={hasChallenges}
            isPremium={isPremium}
            challenges={challenges}
            startOrder={startOrder}
          />
          <LoyaltyHomeRewards
            style={[premiumBgColor, styles.challengesRewardsPaddingSM]}
            hasRewards={hasRewards}
            rewards={rewards}
            startOrder={startOrder}
            showTerms={showTerms}
          />
        </>
      ) : null}

      {/* FAQ (XS Only) */}

      {currentBreakpoint.isXS ? (
        <LoyaltyHomeFaq
          palette={isPremium ? 'cream' : 'kale'}
          showHowItWorksModal={showHowItWorksModal}
          onForgotToScan={isForgotToScanEnabled ? goToForgotToScan : undefined}
        />
      ) : null}

      {/* How It Works (SM+ Only) */}

      {minWidth.isSM ? (
        <Loyalty.Home.HowItWorks.Container
          palette={isPremium ? 'dark' : 'light'}
        >
          <Loyalty.Home.HowItWorks.Content>
            {homeHowItWorksSM.map((entry, index) => (
              <Loyalty.Home.HowItWorks.Card
                index={index + 1}
                key={entry.title}
                title={entry.title}
                description={entry.description}
                palette={isPremium ? 'dark' : 'light'}
              />
            ))}
          </Loyalty.Home.HowItWorks.Content>

          {minWidth.isSM && homeHowItWorksDisclaimer ? (
            <Loyalty.Home.HowItWorks.Disclaimer
              palette={isPremium ? 'dark' : 'light'}
            >
              {homeHowItWorksDisclaimer}
            </Loyalty.Home.HowItWorks.Disclaimer>
          ) : null}
        </Loyalty.Home.HowItWorks.Container>
      ) : null}

      {/* FAQ (SM and Above Only) */}

      {minWidth.isSM ? (
        <LoyaltyHomeFaq
          palette={isPremium ? 'cream' : 'kale'}
          showHowItWorksButton={false}
          style={styles.faqContainerSM}
          onForgotToScan={isForgotToScanEnabled ? goToForgotToScan : undefined}
        />
      ) : null}

      {/* Loyalty Footer */}

      <Loyalty.Home.Footer
        style={premiumBgColor}
        trademark={trademark}
        termsAndConditionsUrl={termsAndConditionsUrl}
        isPremium={isPremium}
        onLinkPress={handleOnLinkPress}
      />

      {/* App Footer */}

      {minWidth.isSM ? <AppFooter /> : null}

      {/* Modals */}

      <LoyaltyPointHistoryModal
        availablePoints={availablePoints}
        visible={isShowingPointsHistoryModal}
        startOrder={startOrder}
        onRequestClose={hidePointsHistoryModal}
      />

      <LoyaltyHowItWorksModal
        visible={isShowingHowItWorksModal}
        onRequestClose={hideHowItWorksModal}
      />

      <ModalDialogue
        visible={Boolean(termsBeingShown)}
        onRequestClose={hideTerms}
      >
        {termsBeingShown?.terms && termsBeingShown?.name ? (
          <LoyaltyRewardTerms
            title={termsBeingShown.name}
            terms={termsBeingShown.terms}
            expirationDate={termsBeingShown?.expirationDate}
          />
        ) : null}
      </ModalDialogue>
    </Loyalty.Home.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  challengesRewardsPaddingXS: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  challengesRewardsPaddingSM: {
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['10'],
  },
  paddingWithContentTop: {
    paddingTop: theme.spacing['2'],
  },
  paddingWithContentBottom: {
    paddingBottom: theme.spacing['2'],
  },
  premiumBenefitsBorder: {
    borderColor: theme.colors.CREAM,
  },
  loyaltyInfoBenefitsOuterContainer: {
    marginTop: 0,
  },
  loyaltyInfoBenefits: {
    marginTop: 0,
    marginBottom: 0,
    marginHorizontal: theme.spacing['6'],
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingBottom: theme.spacing['4'],
  },
  faqContainerSM: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['30'],
    paddingVertical: theme.spacing['20'],
  },
});
